import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '../../utils/cn';
import { fetchCountryCodes, type CountryCode } from '../../services/countryCodesApi';
import { detectUserCountry } from '../../services/detectCountryApi';
import { DEFAULT_PHONE_VALUE, type PhoneValue } from './phoneUtils';

interface PhoneInputProps {
  id?: string;
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  hasError?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /**
   * Visual theme for the trigger + input surface. `dark` matches the glassy
   * inputs on the purple Contact-Us form; the country-picker popup itself
   * always stays on a light card so the searchable list remains readable.
   */
  variant?: 'light' | 'dark';
}

const FALLBACK_COUNTRIES: CountryCode[] = [
  { name: 'India', iso_code: 'IN', dial_code: '+91' },
  { name: 'United States', iso_code: 'US', dial_code: '+1' },
  { name: 'United Kingdom', iso_code: 'GB', dial_code: '+44' },
  { name: 'Canada', iso_code: 'CA', dial_code: '+1' },
  { name: 'Australia', iso_code: 'AU', dial_code: '+61' },
  { name: 'Singapore', iso_code: 'SG', dial_code: '+65' },
  { name: 'United Arab Emirates', iso_code: 'AE', dial_code: '+971' },
  { name: 'Germany', iso_code: 'DE', dial_code: '+49' },
];

let cachedCountries: CountryCode[] | null = null;
let inflight: Promise<CountryCode[]> | null = null;

async function loadCountries(): Promise<CountryCode[]> {
  if (cachedCountries) return cachedCountries;
  if (inflight) return inflight;
  inflight = fetchCountryCodes()
    .then((res) => {
      const list = res.success && res.countries.length > 0 ? res.countries : FALLBACK_COUNTRIES;
      cachedCountries = list;
      return list;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  value,
  onChange,
  hasError,
  disabled,
  placeholder = '9876543210',
  className,
  variant = 'light',
}) => {
  const isDark = variant === 'dark';
  const [countries, setCountries] = useState<CountryCode[]>(FALLBACK_COUNTRIES);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;
    loadCountries().then((list) => {
      if (mounted) setCountries(list);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // ---- Country auto-detection ---------------------------------------
  // On first mount, if the parent hasn't overridden the initial dial
  // code (i.e. the value is still the app-wide default), look up the
  // user's location and switch the picker to their country. Applied
  // exactly once per instance — tracked via a ref so subsequent React
  // re-renders (or countries-list updates) don't retrigger it after
  // the user has manually picked a country.
  const autoDetectAppliedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (autoDetectAppliedRef.current) return;
    if (countries.length === 0) return;

    // Only auto-detect when the parent is still passing the app-wide
    // default. Any prior user selection (or a caller that pre-fills a
    // specific country) is preserved verbatim.
    const isStillDefault =
      value.dialCode === DEFAULT_PHONE_VALUE.dialCode &&
      value.isoCode === DEFAULT_PHONE_VALUE.isoCode;
    if (!isStillDefault) {
      autoDetectAppliedRef.current = true;
      return;
    }

    detectUserCountry().then((iso) => {
      // The ref may have flipped in the meantime if the user tapped
      // the flag menu and picked something before the network call
      // resolved — respect that.
      if (autoDetectAppliedRef.current) return;
      if (!iso) {
        autoDetectAppliedRef.current = true;
        return;
      }
      const match = countries.find((c) => c.iso_code === iso);
      // If the full country list hasn't loaded yet and the fallback
      // list doesn't include the detected country, keep the ref false
      // so the effect re-runs when `countries` updates.
      if (!match) return;
      onChangeRef.current({
        dialCode: match.dial_code,
        isoCode: match.iso_code,
        number: value.number,
      });
      autoDetectAppliedRef.current = true;
    });
    // We deliberately depend only on `countries` so the effect re-runs
    // when the full country list finishes loading (in case the detected
    // ISO wasn't in the fallback). Values like `value.number` are
    // captured in the closure but only read at emission time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) => {
      const dial = c.dial_code.replace('+', '');
      const query = q.replace('+', '');
      return (
        c.name.toLowerCase().includes(q) ||
        c.iso_code.toLowerCase() === q ||
        dial.startsWith(query)
      );
    });
  }, [countries, search]);

  // Prefer matching by ISO code so that shared dial codes (e.g. US and CA both
  // use +1) render the actual country the user picked. Fall back to dial code
  // when the caller only knows the code (e.g. an initial value with no ISO).
  const selected =
    (value.isoCode &&
      (countries.find((c) => c.iso_code === value.isoCode) ??
        FALLBACK_COUNTRIES.find((c) => c.iso_code === value.isoCode))) ||
    countries.find((c) => c.dial_code === value.dialCode) ||
    FALLBACK_COUNTRIES.find((c) => c.dial_code === value.dialCode) ||
    FALLBACK_COUNTRIES[0];

  const handleNumberChange = (raw: string) => {
    const cleaned = raw.replace(/[^\d\s-]/g, '');
    onChange({ dialCode: value.dialCode, isoCode: value.isoCode, number: cleaned });
  };

  const selectCountry = (c: CountryCode) => {
    onChange({ dialCode: c.dial_code, isoCode: c.iso_code, number: value.number });
    setOpen(false);
    setSearch('');
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'relative flex rounded-lg border overflow-visible transition-all',
        isDark ? 'bg-white/10 backdrop-blur-sm' : 'bg-white',
        hasError
          ? isDark
            ? 'border-red-400/70 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-400/30'
            : 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20'
          : isDark
            ? 'border-white/20 hover:border-white/30 focus-within:border-accent-300 focus-within:bg-white/15 focus-within:ring-2 focus-within:ring-accent-400/30'
            : 'border-navy-200 focus-within:border-accent-500 focus-within:ring-2 focus-within:ring-accent-500/20',
        disabled && 'opacity-60 cursor-not-allowed',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={cn(
          'flex items-center gap-1 px-3 border-r font-medium text-sm rounded-l-lg transition-colors',
          isDark
            ? 'border-white/20 text-white hover:bg-white/10'
            : 'border-navy-200 text-navy-800 hover:bg-navy-50',
          disabled && 'cursor-not-allowed',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selected.iso_code}</span>
        <span className={isDark ? 'text-white/70' : 'text-navy-600'}>{selected.dial_code}</span>
        <ChevronDown
          className={cn(
            'w-4 h-4 transition-transform',
            isDark ? 'text-white/60' : 'text-navy-400',
            open && 'rotate-180',
          )}
        />
      </button>

      <input
        id={id}
        type="tel"
        inputMode="tel"
        value={value.number}
        onChange={(e) => handleNumberChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className={cn(
          'flex-1 px-4 py-3 bg-transparent outline-none rounded-r-lg',
          isDark ? 'text-white placeholder-white/40' : 'text-navy-900 placeholder-navy-400',
        )}
      />

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 max-w-full max-h-72 bg-white border border-navy-200 rounded-lg shadow-xl z-50 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-navy-100">
            <div className="relative">
              <Search className="w-4 h-4 text-navy-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country or +code"
                className="w-full pl-8 pr-2 py-2 text-sm border border-navy-200 rounded-md outline-none focus:border-accent-500"
              />
            </div>
          </div>
          <ul className="overflow-y-auto flex-1" role="listbox">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-navy-500">No matches</li>
            ) : (
              filtered.map((c) => (
                <li key={`${c.iso_code}-${c.dial_code}`}>
                  <button
                    type="button"
                    onClick={() => selectCountry(c)}
                    className={cn(
                      'w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-navy-50 transition-colors',
                      c.iso_code === selected.iso_code && 'bg-accent-50 text-accent-700',
                    )}
                  >
                    <span className="truncate">
                      <span className="font-medium text-navy-800 mr-2">{c.iso_code}</span>
                      <span className="text-navy-600">{c.name}</span>
                    </span>
                    <span className="text-navy-500 font-mono text-xs shrink-0 whitespace-nowrap">{c.dial_code}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
